import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'primereact/dialog';
import { Messages } from 'primereact/messages';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import Container from 'react-bootstrap/Container';
import adminApi from '../api/adminApi';
import { useAuth } from '../contexts/AuthContext';
import { canAccessAdmin, FEATURE_FLAGS, hasFeature } from '../utils/accessControl';
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';

const DEFAULT_FEATURE_OPTIONS = [
  FEATURE_FLAGS.NotificationSettings,
  FEATURE_FLAGS.ManageMembers,
  FEATURE_FLAGS.TileTimeLimitation,
];

const DEFAULT_ROLLOUT_ROLE = 'CustomerLevelUser';
const DEFAULT_ROLLOUT_FEATURE = 'tiletimelimitation';

const normalizeFeatureFlags = (featureFlags) => Array.from(
  new Set(
    (Array.isArray(featureFlags) ? featureFlags : [])
      .map((flag) => String(flag || '').trim())
      .filter(Boolean)
  )
).sort((a, b) => a.localeCompare(b));

const setFeatureFlagOnEntity = (entity, featureName, enabled) => {
  if (!entity) return entity;

  const nextFlags = new Set(normalizeFeatureFlags(entity.featureFlags));

  if (enabled) {
    nextFlags.add(featureName);
  } else {
    nextFlags.delete(featureName);
  }

  return {
    ...entity,
    featureFlags: Array.from(nextFlags).sort((a, b) => a.localeCompare(b)),
  };
};

const normalizeUserSummaryRow = (user) => {
  const name = String(user.name || '').trim();
  const surname = String(user.surname || '').trim();
  const fullName = `${name} ${surname}`.trim();

  const customerLabel =
    user.customerId !== null && user.customerId !== undefined && String(user.customerName || '').trim()
      ? String(user.customerName).trim()
      : '';
  const appProviderLabel =
    user.appProviderId !== null && user.appProviderId !== undefined && String(user.appProviderName || '').trim()
      ? String(user.appProviderName).trim()
      : '';

  return {
    ...user,
    fullName,
    accountLabel: [customerLabel, appProviderLabel].filter(Boolean).join(' / '),
    featureFlags: normalizeFeatureFlags(user.featureFlags),
  };
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const msgs = useRef(null);
  const toast = useRef(null);
  const { accessContext, refreshAccessContext } = useAuth();
  const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect();
  const isAdmin = canAccessAdmin(accessContext);

  const [loading, setLoading] = useState(true);
  const [metaLoading, setMetaLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [rowFlagUpdating, setRowFlagUpdating] = useState({});
  const [search, setSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  const [roles, setRoles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editLocations, setEditLocations] = useState([]);
  const [editLocationsLoading, setEditLocationsLoading] = useState(false);
  const [appProviders, setAppProviders] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState([]);

  const [manageVisible, setManageVisible] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleRolloutVisible, setRoleRolloutVisible] = useState(false);
  const [roleRolloutSaving, setRoleRolloutSaving] = useState(false);
  const [roleRolloutRole, setRoleRolloutRole] = useState(DEFAULT_ROLLOUT_ROLE);
  const [roleRolloutFeature, setRoleRolloutFeature] = useState(DEFAULT_ROLLOUT_FEATURE);
  const [roleRolloutEnabled, setRoleRolloutEnabled] = useState(true);

  const [testerEnabled, setTesterEnabled] = useState(false);

  const [editForm, setEditForm] = useState({
    name: '',
    surname: '',
    email: '',
    role: '',
    customerId: null,
    locationId: null,
    appProviderId: null,
  });

  const isEditCustomerRole = editForm.role === 'CustomerLevelUser';
  const isEditLocationRole = editForm.role === 'LocationLevelUser';
  const isEditAppProviderRole = editForm.role === 'AppProviderUser' || editForm.role === 'AppProviderLevelUser';

  const roleLookupOptions = useMemo(() => (roles || []).map((r) => ({ label: r, value: r })), [roles]);
  const customerOptions = useMemo(
    () => (customers || []).map((c) => ({ label: c.name, value: c.id })),
    [customers]
  );
  const appProviderOptions = useMemo(
    () => (appProviders || []).map((a) => ({ label: a.name, value: a.id })),
    [appProviders]
  );
  const editLocationOptions = useMemo(
    () => (editLocations || []).map((location) => ({ label: location.name, value: location.id })),
    [editLocations]
  );

  const roleOptions = useMemo(() => {
    const baseRoles = Array.isArray(roles) && roles.length > 0
      ? roles
      : Array.from(new Set((users || []).map((u) => String(u.role || '')).filter(Boolean)));

    return [{ label: 'All Roles', value: '' }, ...baseRoles.map((r) => ({ label: r, value: r }))];
  }, [roles, users]);

  const roleRolloutRoleOptions = useMemo(() => {
    const baseRoles = Array.isArray(roles) && roles.length > 0
      ? roles
      : Array.from(new Set((users || []).map((u) => String(u.role || '')).filter(Boolean)));

    return baseRoles.map((r) => ({ label: r, value: r }));
  }, [roles, users]);

  const roleRolloutFeatureOptions = useMemo(() => {
    return Array.from(
      new Set([...DEFAULT_FEATURE_OPTIONS, ...(availableFeatures || []), DEFAULT_ROLLOUT_FEATURE])
    )
      .map((featureName) => String(featureName || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((featureName) => ({ label: featureName, value: featureName }));
  }, [availableFeatures]);

  const featureRows = useMemo(() => {
    const enabledFlags = new Set(normalizeFeatureFlags(selectedUser && selectedUser.featureFlags));

    return Array.from(new Set([...DEFAULT_FEATURE_OPTIONS, ...(availableFeatures || []), ...Array.from(enabledFlags)]))
      .sort((a, b) => a.localeCompare(b))
      .map((featureName) => ({
        name: featureName,
        enabled: enabledFlags.has(featureName),
      }));
  }, [availableFeatures, selectedUser]);

  const filteredUsers = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();

    return (users || []).filter((u) => {
      if (selectedRole && String(u.role || '') !== selectedRole) return false;

      if (!q) return true;

      // Use raw email for filtering/search (emailMasked may be ignored on this admin page).
      const haystack = [u.name, u.surname, u.fullName, u.email, u.customerName, u.appProviderName, u.accountLabel]
        .map((x) => String(x || '').toLowerCase())
        .join(' ');

      return haystack.includes(q);
    });
  }, [users, search, selectedRole]);

  const handleError = (error, fallbackMessage) => {
    if (handleAccessError(error)) {
      return;
    }

    const detail =
      (error && error.response && error.response.data) ||
      (error && error.message) ||
      fallbackMessage;

    if (msgs.current) {
      msgs.current.show({ severity: 'error', summary: '', detail, sticky: true });
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      clearAccessIssue();
      if (msgs.current) msgs.current.clear();
      const result = await adminApi.getUsersSummary();
      const normalized = (Array.isArray(result) ? result : []).map(normalizeUserSummaryRow);

      const featureFlagResults = await Promise.allSettled(
        normalized.map(async (user) => ({
          userId: user.id,
          featureFlags: await adminApi.getUserFeatureFlags(user.id),
        }))
      );

      const featureFlagsByUserId = new Map();
      featureFlagResults.forEach((resultItem) => {
        if (resultItem.status !== 'fulfilled') return;
        featureFlagsByUserId.set(resultItem.value.userId, normalizeFeatureFlags(resultItem.value.featureFlags));
      });

      setUsers(
        normalized.map((user) => ({
          ...user,
          featureFlags: featureFlagsByUserId.get(user.id) || user.featureFlags || [],
        }))
      );
    } catch (error) {
      handleError(error, 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  const loadMeta = async () => {
    try {
      setMetaLoading(true);
      clearAccessIssue();
      const [rolesResp, customersResp, appProvidersResp, featuresResp] = await Promise.all([
        adminApi.getRoles(),
        adminApi.getCustomersLookup(),
        adminApi.getAppProvidersLookup(),
        adminApi.getFeatures(),
      ]);

      setRoles(Array.isArray(rolesResp) ? rolesResp : []);
      setCustomers(Array.isArray(customersResp) ? customersResp : []);
      setAppProviders(Array.isArray(appProvidersResp) ? appProvidersResp : []);
      setAvailableFeatures(
        Object.keys(featuresResp && typeof featuresResp === 'object' ? featuresResp : {})
          .map((featureName) => String(featureName || '').trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b))
      );
    } catch (error) {
      handleError(error, 'Failed to load user metadata.');
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadMeta();
  }, [location.state && location.state.refreshAt]);

  useEffect(() => {
    const refreshCatalog = () => loadMeta();
    window.addEventListener('smarttile:catalog-updated', refreshCatalog);
    return () => window.removeEventListener('smarttile:catalog-updated', refreshCatalog);
  }, []);

  useEffect(() => {
    if (location.state && location.state.flashSuccess && toast.current) {
      toast.current.show({
        severity: 'success',
        summary: '',
        detail: String(location.state.flashSuccess),
      });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const refreshSingleUser = async (userId) => {
    try {
      const detail = await adminApi.getUserById(userId);
      setUsers((prev) => prev.map((u) => (u.id === userId ? normalizeUserSummaryRow({ ...u, ...detail }) : u)));
    } catch (_) {
      await loadUsers();
    }
  };

  const updateUserFeatureFlagsInState = (userId, featureFlags) => {
    const normalizedFlags = normalizeFeatureFlags(featureFlags);

    setUsers((prev) => prev.map((user) => (
      user.id === userId
        ? {
            ...user,
            featureFlags: normalizedFlags,
          }
        : user
    )));

    setSelectedUser((prev) => (
      prev && prev.id === userId
        ? {
            ...prev,
            featureFlags: normalizedFlags,
          }
        : prev
    ));
  };

  const openManageDialog = async (user) => {
    setManageVisible(true);
    setManageLoading(true);
    setSelectedUser(user);
    setTesterEnabled(Boolean(user.notificationSettingsTester));

    try {
      const detail = await adminApi.getUserById(user.id);
      const hydratedUser = { ...user, ...(detail || {}) };
      setSelectedUser(hydratedUser);
      setTesterEnabled(Boolean(hydratedUser.notificationSettingsTester));
    } catch (error) {
      handleError(error, 'Failed to load user feature details.');
    } finally {
      setManageLoading(false);
    }
  };

  const loadEditLocations = async (customerId) => {
    if (customerId === null || customerId === undefined) {
      setEditLocations([]);
      return;
    }

    try {
      setEditLocationsLoading(true);
      const locations = await adminApi.getLocationsByCustomerLookup(customerId);
      setEditLocations(Array.isArray(locations) ? locations : []);
    } catch (error) {
      setEditLocations([]);
      handleError(error, 'Failed to load locations for this customer.');
    } finally {
      setEditLocationsLoading(false);
    }
  };

  const openEditDialog = async (user) => {
    setSelectedUser(user);
    setEditLocations([]);
    setEditForm({
      name: user.name || '',
      surname: user.surname || '',
      email: user.email || '',
      role: user.role || '',
      customerId: user.customerId !== undefined && user.customerId !== null ? Number(user.customerId) : null,
      locationId: user.locationId !== undefined && user.locationId !== null ? Number(user.locationId) : null,
      appProviderId: user.appProviderId !== undefined && user.appProviderId !== null ? Number(user.appProviderId) : null,
    });
    setEditVisible(true);

    if (user.role !== 'LocationLevelUser') return;

    try {
      const detail = await adminApi.getUserById(user.id);
      const hydratedUser = { ...user, ...(detail || {}) };
      const customerId = hydratedUser.customerId !== undefined && hydratedUser.customerId !== null ? Number(hydratedUser.customerId) : null;
      const locationId = hydratedUser.locationId !== undefined && hydratedUser.locationId !== null ? Number(hydratedUser.locationId) : null;

      setSelectedUser(hydratedUser);
      setEditForm((prev) => ({ ...prev, customerId, locationId }));
      await loadEditLocations(customerId);
    } catch (error) {
      handleError(error, 'Failed to load user location details.');
    }
  };

  const onEditRoleChange = (role) => {
    setEditForm((prev) => ({
      ...prev,
      role,
      customerId: null,
      locationId: null,
      appProviderId: null,
    }));
    setEditLocations([]);
  };

  const onEditLocationCustomerChange = async (customerId) => {
    setEditForm((prev) => ({ ...prev, customerId, locationId: null }));
    await loadEditLocations(customerId);
  };

  const saveTesterStatus = async () => {
    if (!selectedUser) return;

    try {
      await adminApi.setNotificationSettingsTester(selectedUser.id, testerEnabled);
      await refreshAccessContext();
      await refreshSingleUser(selectedUser.id);
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: '',
          detail: 'Tester access updated.',
        });
      }
    } catch (error) {
      handleError(error, 'Failed to update tester access.');
    }
  };

  const saveFeatureFlag = async (featureName, enabled) => {
    if (!selectedUser) return;
    const safeName = String(featureName || '').trim();
    if (!safeName) return;

    try {
      await adminApi.setUserFeatureFlag(selectedUser.id, safeName, enabled);
      await refreshAccessContext();
      updateUserFeatureFlagsInState(
        selectedUser.id,
        setFeatureFlagOnEntity(selectedUser, safeName, enabled).featureFlags
      );
      await refreshSingleUser(selectedUser.id);
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: '',
          detail: `Feature '${safeName}' updated for user.`,
        });
      }
    } catch (error) {
      handleError(error, 'Failed to update user feature flag.');
    }
  };

  const toggleTileTimeLimitation = async (user, enabled) => {
    if (!user || user.id === null || user.id === undefined) return;

    const previousFeatureFlags = normalizeFeatureFlags(user.featureFlags);
    const optimisticUser = setFeatureFlagOnEntity(user, FEATURE_FLAGS.TileTimeLimitation, enabled);

    setRowFlagUpdating((prev) => ({ ...prev, [user.id]: true }));
    updateUserFeatureFlagsInState(user.id, optimisticUser.featureFlags);

    try {
      await adminApi.setTileTimeLimitationFeatureFlag(user.id, enabled);
      await refreshAccessContext();
      const nextFeatureFlags = await adminApi.getUserFeatureFlags(user.id);
      updateUserFeatureFlagsInState(user.id, nextFeatureFlags);
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: '',
          detail: `Tile Time Limitation ${enabled ? 'enabled' : 'disabled'} for ${user.email || user.fullName || user.id}.`,
        });
      }
    } catch (error) {
      updateUserFeatureFlagsInState(user.id, previousFeatureFlags);
      if (toast.current) {
        toast.current.show({
          severity: 'error',
          summary: '',
          detail:
            (error && error.response && error.response.data) ||
            (error && error.message) ||
            'Failed to update Tile Time Limitation.',
          life: 5000,
        });
      }
    } finally {
      setRowFlagUpdating((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
    }
  };

  const saveRoleFeatureRollout = async () => {
    const safeRole = String(roleRolloutRole || '').trim();
    const safeFeature = String(roleRolloutFeature || '').trim();

    if (!safeRole || !safeFeature) {
      if (msgs.current) {
        msgs.current.show({
          severity: 'warn',
          summary: '',
          detail: 'Please select user level type and feature name.',
        });
      }
      return;
    }

    try {
      setRoleRolloutSaving(true);
      await adminApi.setRoleFeatureFlag(safeRole, safeFeature, Boolean(roleRolloutEnabled));
      setRoleRolloutVisible(false);

      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: '',
          detail: `Feature '${safeFeature}' ${roleRolloutEnabled ? 'enabled' : 'disabled'} for all ${safeRole} accounts.`,
        });
      }
    } catch (error) {
      if (toast.current) {
        toast.current.show({
          severity: 'error',
          summary: '',
          detail:
            (error && error.response && error.response.data) ||
            (error && error.message) ||
            'Failed to update role feature rollout.',
          life: 5000,
        });
      }
    } finally {
      setRoleRolloutSaving(false);
    }
  };

  const saveUserUpdate = async () => {
    if (!selectedUser) return;

    try {
      if (!String(editForm.role || '').trim()) {
        if (msgs.current) {
          msgs.current.show({ severity: 'warn', summary: '', detail: 'Role is required.' });
        }
        return;
      }

      if (isEditCustomerRole && (editForm.customerId === null || editForm.customerId === undefined)) {
        if (msgs.current) {
          msgs.current.show({ severity: 'warn', summary: '', detail: 'Customer is required for CustomerLevelUser.' });
        }
        return;
      }

      if (isEditLocationRole && (editForm.customerId === null || editForm.customerId === undefined)) {
        if (msgs.current) {
          msgs.current.show({ severity: 'warn', summary: '', detail: 'Customer is required for LocationLevelUser.' });
        }
        return;
      }

      if (isEditLocationRole && (editForm.locationId === null || editForm.locationId === undefined)) {
        if (msgs.current) {
          msgs.current.show({ severity: 'warn', summary: '', detail: 'Location is required for LocationLevelUser.' });
        }
        return;
      }

      if (isEditAppProviderRole && (editForm.appProviderId === null || editForm.appProviderId === undefined)) {
        if (msgs.current) {
          msgs.current.show({ severity: 'warn', summary: '', detail: 'App Provider is required for AppProvider role.' });
        }
        return;
      }

      const payload = {
        name: editForm.name,
        surname: editForm.surname,
        email: editForm.email,
        role: editForm.role,
        customerId: isEditCustomerRole || isEditLocationRole ? Number(editForm.customerId) : null,
        locationId: isEditLocationRole ? Number(editForm.locationId) : null,
        appProviderId: isEditAppProviderRole ? Number(editForm.appProviderId) : null,
      };
      await adminApi.updateUser(selectedUser.id, payload);
      setEditVisible(false);
      await loadUsers();
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: '',
          detail: 'User updated successfully.',
        });
      }
    } catch (error) {
      handleError(error, 'Failed to update user.');
    }
  };

  const deleteUser = async (user) => {
    const confirmed = window.confirm(
      `Delete user '${user.email || user.emailMasked || user.id}'? This action is irreversible.`
    );
    if (!confirmed) return;

    try {
      await adminApi.deleteUser(user.id);
      await loadUsers();
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: '', detail: 'User deleted.' });
      }
    } catch (error) {
      handleError(error, 'Failed to delete user.');
    }
  };

  const fullNameTemplate = (row) => String(row.fullName || `${row.name || ''} ${row.surname || ''}`).trim();

  const accountTemplate = (row) => {
    if (row && row.accountLabel) return row.accountLabel;
    const customer =
      row && row.customerId !== null && row.customerId !== undefined && row.customerName ? String(row.customerName).trim() : '';
    const appProvider =
      row && row.appProviderId !== null && row.appProviderId !== undefined && row.appProviderName
        ? String(row.appProviderName).trim()
        : '';
    return [customer, appProvider].filter(Boolean).join(' / ');
  };

  const featureFlagsTemplate = () => {
    if (!selectedUser) return 'Unknown';
    if (!Array.isArray(selectedUser.featureFlags)) return 'Unknown until saved';
    if (selectedUser.featureFlags.length === 0) return 'None';
    return selectedUser.featureFlags.join(', ');
  };

  const tileTimeLimitationTemplate = (row) => {
    const enabled = hasFeature(FEATURE_FLAGS.TileTimeLimitation)(row);
    const pending = Boolean(rowFlagUpdating[row.id]);

    return (
      <div className="flex align-items-center gap-2">
        <span>{enabled ? 'ON' : 'OFF'}</span>
        <InputSwitch
          checked={enabled}
          onChange={(e) => toggleTileTimeLimitation(row, Boolean(e.value))}
          disabled={pending}
        />
      </div>
    );
  };

  const actionsTemplate = (row) => (
    <div className="flex align-items-center">
      <Button
        label="Manage Features"
        icon="pi pi-sliders-h"
        className="p-button-text"
        onClick={() => openManageDialog(row)}
      />
      <Button
        label="Edit"
        icon="pi pi-pencil"
        className="p-button-text"
        onClick={() => openEditDialog(row)}
      />
      <Button
        label="Delete"
        icon="pi pi-trash"
        className="p-button-text p-button-danger"
        onClick={() => deleteUser(row)}
      />
    </div>
  );

  const progressbar = (
    <div className="card mt-3">
      <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>
  );

  if (accessIssue) {
    return <AccessState {...accessIssue} />;
  }

  return (
    <Container fluid className="justify-content-md-center mt-5">
      <div className="flex justify-content-center mt-3">
        <Container className="flex align-content-center flex-wrap">
          <Button link icon="pi pi-arrow-left" onClick={() => navigate('/customers')} className="flex mb-3" />
          <h4 className="flex mt-2 mb-3 ml-3 mr-3">Admin - Users</h4>

          <div>
            <div className="flex align-items-center flex-wrap gap-3 mb-3">
              <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name/surname/email"
                />
              </span>

              <Dropdown
                value={selectedRole}
                options={roleOptions}
                onChange={(e) => setSelectedRole(e.value)}
                placeholder="Filter by role"
                style={{ minWidth: '220px' }}
              />

              <Button
                label="Create User"
                icon="pi pi-user-plus"
                className="p-button-success"
                onClick={() => navigate('/admin/users/create')}
              />

              <Button
                label="Global Features"
                icon="pi pi-cog"
                className="p-button-text"
                onClick={() => navigate('/admin/features')}
              />
            </div>
          </div>
        </Container>
      </div>

      <Messages ref={msgs} />
      <Toast ref={toast} />

      {isAdmin && (
        <div className="card p-3 mb-3">
          <div className="flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <h5 className="mb-2">Feature Rollout By User Level</h5>
              <div className="text-muted mb-1">Add a role-based feature rollout and apply it to all accounts in that user level.</div>
            </div>
            <Button
              label="Add New Feature"
              icon="pi pi-plus"
              className="p-button-success"
              onClick={() => setRoleRolloutVisible(true)}
              disabled={roleRolloutSaving}
            />
          </div>
        </div>
      )}

      {(loading || metaLoading) && progressbar}

      <DataTable
        value={filteredUsers}
        dataKey="id"
        style={{ width: '100%' }}
        tableStyle={{ minWidth: '90rem' }}
        emptyMessage={loading ? 'Loading users...' : 'No users found.'}
        scrollable
        scrollHeight="calc(100vh - 240px)"
      >
        <Column
          field="fullName"
          header="User"
          body={fullNameTemplate}
          style={{ minWidth: '16rem' }}
          sortable
        ></Column>
        <Column field="email" header="Email" style={{ minWidth: '14rem' }} sortable></Column>
        <Column field="role" header="Role" style={{ minWidth: '12rem' }} sortable></Column>
        <Column
          field="accountLabel"
          header="Account"
          body={accountTemplate}
          style={{ minWidth: '18rem' }}
          sortable
        ></Column>
        {isAdmin && (
          <Column
            header="Tile Time Limitation"
            body={tileTimeLimitationTemplate}
            style={{ minWidth: '14rem' }}
          ></Column>
        )}
        <Column header="Actions" body={actionsTemplate} style={{ minWidth: '20rem' }}></Column>
      </DataTable>

        <Dialog
          header={selectedUser ? `Manage Features - ${selectedUser.email || selectedUser.emailMasked || selectedUser.id}` : 'Manage Features'}
          visible={manageVisible}
          onHide={() => setManageVisible(false)}
          style={{ width: '650px' }}
          modal
        >
          <div className="mb-3">
            <strong>Current feature flags:</strong> {featureFlagsTemplate()}
          </div>

          {manageLoading && progressbar}

          <div className="card p-3 mb-3">
            <div className="flex align-items-center justify-content-between">
              <div>
                <strong>Notification Settings Tester</strong>
                <div>
                  Current status:{' '}
                  {selectedUser && selectedUser.notificationSettingsTester !== undefined
                    ? String(Boolean(selectedUser.notificationSettingsTester))
                    : 'Unknown until saved'}
                </div>
              </div>
              <InputSwitch checked={testerEnabled} onChange={(e) => setTesterEnabled(e.value)} disabled={manageLoading} />
            </div>
            <Button
              label="Save Tester Access"
              icon="pi pi-check"
              className="mt-3"
              onClick={saveTesterStatus}
              disabled={manageLoading}
            />
          </div>

          <div className="card p-3">
            <h5>Per-User Feature Flags</h5>
            {!manageLoading && featureRows.length === 0 && <p>No feature flags are available.</p>}
            {featureRows.map((feature) => (
              <div key={feature.name} className="flex align-items-center justify-content-between mb-3">
                <div>
                  <strong>{feature.name}</strong>
                </div>
                <div className="flex align-items-center gap-2">
                  <span>{feature.enabled ? 'Enabled' : 'Disabled'}</span>
                  <InputSwitch
                    checked={feature.enabled}
                    onChange={(e) => saveFeatureFlag(feature.name, Boolean(e.value))}
                    disabled={manageLoading}
                  />
                </div>
              </div>
            ))}
            <small>Profile-backed feature access updates on refresh and next sign-in.</small>
          </div>
        </Dialog>

      <Dialog
        header="Add Role Feature Rollout"
        visible={roleRolloutVisible}
        onHide={() => {
          if (!roleRolloutSaving) setRoleRolloutVisible(false);
        }}
        style={{ width: '560px' }}
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setRoleRolloutVisible(false)}
              disabled={roleRolloutSaving}
            />
            <Button
              label={roleRolloutSaving ? 'Saving...' : 'Save'}
              icon="pi pi-check"
              onClick={saveRoleFeatureRollout}
              disabled={roleRolloutSaving}
            />
          </div>
        }
      >
        <div className="p-fluid">
          <div className="field mb-3">
            <label htmlFor="rollout-role">User level type</label>
            <Dropdown
              id="rollout-role"
              value={roleRolloutRole}
              options={roleRolloutRoleOptions}
              onChange={(e) => setRoleRolloutRole(e.value)}
              placeholder="Select user level type"
              disabled={roleRolloutSaving}
              style={{ minWidth: '320px' }}
            />
          </div>

          <div className="field mb-3">
            <label htmlFor="rollout-feature">Feature name</label>
            <Dropdown
              id="rollout-feature"
              value={roleRolloutFeature}
              options={roleRolloutFeatureOptions}
              onChange={(e) => setRoleRolloutFeature(e.value)}
              placeholder="Select feature"
              filter
              disabled={roleRolloutSaving}
              style={{ minWidth: '320px' }}
            />
          </div>

          <div className="field mb-1">
            <label htmlFor="rollout-enabled" className="mr-3">Enabled</label>
            <InputSwitch
              id="rollout-enabled"
              checked={roleRolloutEnabled}
              onChange={(e) => setRoleRolloutEnabled(Boolean(e.value))}
              disabled={roleRolloutSaving}
            />
            <small className="ml-2">{roleRolloutEnabled ? 'Enable for selected user level' : 'Disable for selected user level'}</small>
          </div>
        </div>
      </Dialog>

      <Dialog
          header={selectedUser ? `Edit User - ${selectedUser.email || selectedUser.emailMasked || selectedUser.id}` : 'Edit User'}
          visible={editVisible}
          onHide={() => setEditVisible(false)}
          style={{ width: '600px' }}
          modal
          footer={
            <div>
              <Button label="Cancel" className="p-button-text" onClick={() => setEditVisible(false)} />
              <Button label="Save" icon="pi pi-check" onClick={saveUserUpdate} />
            </div>
          }
        >
          <div className="p-fluid">
            <div className="field mb-3">
              <label htmlFor="name">Name</label>
              <InputText
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="field mb-3">
              <label htmlFor="surname">Surname</label>
              <InputText
                id="surname"
                value={editForm.surname}
                onChange={(e) => setEditForm((prev) => ({ ...prev, surname: e.target.value }))}
              />
            </div>
            <div className="field mb-3">
              <label htmlFor="email">Email</label>
              <InputText
                id="email"
                value={editForm.email}
                onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="field mb-3">
              <label htmlFor="role">Role</label>
              <Dropdown
                id="role"
                value={editForm.role}
                options={roleLookupOptions}
                onChange={(e) => onEditRoleChange(e.value)}
                placeholder="Select role"
                style={{ minWidth: '280px' }}
              />
            </div>

            {(isEditCustomerRole || isEditLocationRole) && (
              <div className="field mb-3">
                <label htmlFor="edit-customer">Customer</label>
                <Dropdown
                  id="edit-customer"
                  value={editForm.customerId}
                  options={customerOptions}
                  onChange={(e) => isEditLocationRole ? onEditLocationCustomerChange(e.value) : setEditForm((prev) => ({ ...prev, customerId: e.value }))}
                  placeholder="Select customer"
                  filter
                  style={{ minWidth: '320px' }}
                />
              </div>
            )}

            {isEditLocationRole && (
              <div className="field mb-3">
                <label htmlFor="edit-location">Location</label>
                <Dropdown
                  id="edit-location"
                  value={editForm.locationId}
                  options={editLocationOptions}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, locationId: e.value }))}
                  placeholder={editLocationsLoading ? 'Loading locations...' : 'Select location'}
                  disabled={editForm.customerId === null || editForm.customerId === undefined || editLocationsLoading}
                  filter
                  style={{ minWidth: '320px' }}
                />
              </div>
            )}

            {isEditAppProviderRole && (
              <div className="field mb-3">
                <label htmlFor="edit-appprovider">App Provider</label>
                <Dropdown
                  id="edit-appprovider"
                  value={editForm.appProviderId}
                  options={appProviderOptions}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, appProviderId: e.value }))}
                  placeholder="Select app provider"
                  filter
                  style={{ minWidth: '320px' }}
                />
              </div>
            )}
          </div>
        </Dialog>
    </Container>
  );
};

export default AdminUsers;
